import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    CircularProgress,
    IconButton,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import { useAuth } from '../context/AuthContext';

const CommentsModal = ({ open, onClose, contentId, onCommentAdded }) => {
    const { user: currentUser } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && contentId) {
            loadComments();
        }
    }, [open, contentId]);

    const loadComments = async () => {
        try {
            setLoading(true);
            const response = await contentService.getComments(contentId);
            if (response.success) {
                setComments(response.data);
            } else {
                toast.error('Failed to load comments');
            }
        } catch (error) {
            console.error('Load comments error:', error);
            toast.error('Failed to load comments');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitComment = async () => {

        
            if (!currentUser) {
                toast.info('Please log in to comment');
                return;
            }
            if (!commentText.trim()) {
                toast.error('Please enter a comment');
                return;
            }

            try {
                setSubmitting(true);
                const response = await contentService.addComment(contentId, commentText.trim());
                if (response.success) {
                    // Add new comment to the list
                    const newComment = {
                        CommentId: response.data.commentId,
                        CommentText: response.data.commentText,
                        CustomerName: response.data.customerName,
                        CreatedAt: response.data.createdAt
                    };
                    setComments(prev => [newComment, ...prev]);
                    setCommentText('');
                    toast.success('Comment added successfully');

                    // Notify parent component
                    if (onCommentAdded) {
                        onCommentAdded(contentId);
                    }
                } else {
                    toast.error(response.message || 'Failed to add comment');
                }
            } catch (error) {
                console.error('Add comment error:', error);
                toast.error('Failed to add comment');
            } finally {
                setSubmitting(false);
            }
        };

        const handleKeyPress = (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSubmitComment();
            }
        };

        const formatDate = (dateString) => {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = (now - date) / (1000 * 60 * 60);

            if (diffInHours < 1) {
                return 'Just now';
            } else if (diffInHours < 24) {
                return `${Math.floor(diffInHours)}h ago`;
            } else if (diffInHours < 168) { // 7 days
                return `${Math.floor(diffInHours / 24)}d ago`;
            } else {
                return date.toLocaleDateString();
            }
        };

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Typography variant="h6">
                        Comments ({comments.length})
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 0
                }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : comments.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                No comments yet. Be the first to comment!
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {comments.map((comment, index) => (
                                <React.Fragment key={comment.CommentId}>
                                    <ListItem alignItems="flex-start" sx={{ px: 3, py: 2 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                                                {comment.CustomerName?.charAt(0)?.toUpperCase() || 'U'}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {comment.CustomerName || 'Anonymous'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDate(comment.CreatedAt)}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                    {comment.CommentText}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                    {index < comments.length - 1 && <Divider variant="inset" component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={3}
                            placeholder="Write a comment..."
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={submitting}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSubmitComment}
                            disabled={submitting || !commentText.trim()}
                            sx={{
                                minWidth: 'auto',
                                px: 2,
                                borderRadius: 3
                            }}
                        >
                            {submitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <SendIcon />
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        );
    };

    export default CommentsModal;